import { Card, CardContent, Grid, Icon, IconButton, Tooltip, Typography } from '@mui/material';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { DeleteButton } from '../../../components/delete-button.component';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { useFormContext, useWatch } from 'react-hook-form';
import { Icons } from '../../../assets/icons';
import { ApiResponse } from '../../../models/api-response';
import { useLoadData } from '../../../hooks/useLoadData';
import { useEffect, useRef } from 'react';
import { ValueSetter } from 'date-fns/parse/_lib/Setter';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';

interface BuildingFloorUnitFormProps {
  name: string;
  index?: number;
  onRemove?: (index: number) => void;
  isEnabled?: boolean;
  page?: string;
  syncWithDux: any;
}

export const BuildingFloorUnitForm = ({
  name,
  index,
  onRemove,
  isEnabled = false,
  page,
  syncWithDux
}: BuildingFloorUnitFormProps) => {
  const { getValues, setValue, watch, setError, clearErrors } = useFormContext();
  const navigate = useNavigate();
  const { data, isLoading } = useLoadData<ApiResponse<any>>(
    projectsApiKeyStore.generateLotName(getValues('projectID')),
    isEnabled
  );

  const jobsiteRef = useRef<string | undefined>(getValues(`${name}.jobsite`));

  useWatch({
    name: `${name}.unitName` || [],
  });

  // useEffect(() => {
  //   if (data && data?.data) {
  //     setValue(`${name}.unitName`, data?.data?.unitName);
  //   }
  // }, [data]);

  const handleUnitNameChange = (value: string) => {
    let initialJobNumber = jobsiteRef.current || getValues(`${name}.jobsite`);
    if (!initialJobNumber) {
      initialJobNumber = '';
    }
    const newJobNumber = `${initialJobNumber}${value}`.trim();
    // console.log(12, newJobNumber);
    setValue(`${name}.jobsite`, newJobNumber);



    //Check Duplicate value
    if (!value || value?.trim() === "") {
      clearErrors(`${name}.unitName`);
      return;
    }

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );

    // 2. (Optional) Remove duplicates by unitID
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) =>
        index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );

    const allUnits = uniqueLots?.map((u: any) => u?.unitName?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      setError(`${name}.unitName`, {
        type: "manual",
        message: "Lot/Unit name already exists.",
      });
    } else {
      clearErrors(`${name}.unitName`);
    }
  };

  const handleJobnameNameChange = (value: any) => {
    jobsiteRef.current = value;

    //Check Duplicate value
    if (!value || value?.trim() === "") {
      clearErrors(`${name}.jobsite`);
      return;
    }

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );

    // 2. (Optional) Remove duplicates by unitID
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) =>
        index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );

    const allUnits = uniqueLots?.map((u: any) => u?.jobsite?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      setError(`${name}.jobsite`, {
        type: "manual",
        message: "Jobsite already exists.",
      });
    } else {
      clearErrors(`${name}.jobsite`);
    }
  };

  const handleJobNumberChange = (value: any) => {

    //Check Duplicate value
    if (!value || value?.trim() === "") {
      clearErrors(`${name}.jobNumber`);
      return;
    }

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );

    // 2. (Optional) Remove duplicates by unitID
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) =>
        index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );

    const allUnits = uniqueLots?.map((u: any) => u?.jobNumber?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      setError(`${name}.jobNumber`, {
        type: "manual",
        message: "Job number already exists.",
      });
    } else {
      clearErrors(`${name}.jobNumber`);
    }
  };

  const handleAddressChange = (value: any) => {

    //Check Duplicate value
    if (!value || value?.trim() === "") {
      clearErrors(`${name}.address`);
      return;
    }

    const mergedLots = getValues('projectBuildings')?.flatMap((building: any) =>
      building.buildingFloors.flatMap((floor: any) => floor.buildingLots)
    );

    // 2. (Optional) Remove duplicates by unitID
    const uniqueLots = mergedLots.filter(
      (lot: any, index: any, self: any) =>
        index === self.findIndex((l: any) => l.unitID === lot.unitID)
    );

    const allUnits = uniqueLots?.map((u: any) => u?.address?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      setError(`${name}.address`, {
        type: "manual",
        message: "Address already exists.",
      });
    } else {
      clearErrors(`${name}.address`);
    }
  };

  const projectSyncWithDUX = watch(`${name}.projectSyncWithDUX`);

  return (
    <Card elevation={0} className="floorUnitBlockWrapper" style={{ marginBottom: '0', background: 'none' }}>
      <CardContent className="  " style={{ paddingTop: '4px', paddingBottom: '4px', paddingLeft: '4px' }}>
        <Grid container spacing={2}>
          <Grid item lg={8} sm={8} xl={8} xs={8}>
            <div style={{ display: 'none' }}>
              <label>{getValues(`${name}.unitNumber`)}</label>
            </div>
            <Grid container spacing={2}>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput
                  // disabled={true}
                  name={`${name}.jobsite`}
                  // isShrink={!!watch(`${name}.jobsite`)}
                  label={'Jobsite'}
                  onChange={(e: any) => handleJobnameNameChange(e)}
                  isCustomError={true}
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                      getValues('projectID'),
                      '',
                      getValues(`${name}.unitID`),
                      '',
                      value
                    )
                  }
                />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput
                  name={`${name}.unitName`}
                  label="Lot/Unit"
                  isCustomError={true}
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddressUnitName(
                      getValues('projectID'),
                      '',
                      getValues(`${name}.unitID`),
                      value,
                      ''
                    )
                  }
                  onChange={(e: any) => handleUnitNameChange(e)}
                />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput
                  name={`${name}.jobNumber`}
                  label={'Builder Job Number'}
                  onChange={(e: any) => handleJobNumberChange(e)}
                  isCustomError={true}
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddressJobNumber(
                      getValues('projectID'),
                      '',
                      getValues(`${name}.unitID`),
                      '',
                      '',
                      value
                    )
                  }
                />
              </Grid>
              <Grid item lg={2.3} sm={2.3} xl={2.3} xs={2.3}>
                <FormInput
                  name={`${name}.address`}
                  label="Address"
                  onChange={(e: any) => handleAddressChange(e)}
                  isCustomError={true}
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddressOfAddress(
                      getValues('projectID'),
                      value,
                      getValues(`${name}.unitID`),
                      '',
                      ''
                    )
                  }
                />
              </Grid>
              <Grid item lg={1.7} sm={1.7} xl={1.7} xs={1.7}>
                <FormInput name={`${name}.squareFeet`} label={'Square Feet'} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormAutoCompleteSelectDynamic
                  name={`${name}.proctorResult`}
                  label="Proctor Result "
                  getQueryKeyConfig={() => proctorResultApiKeyStore.getProjectProctorResults(getValues('projectID'))}
                  optionsValueKey="proctorResultsID"
                  optionsLabelKey="proctorFileName"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={4} sm={4} xl={4} xs={4}>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "flex-start",
                alignItems: "flex-start",
              }}
            >
              <FormCheckbox label="Create WO" name={`${name}.createWO`} />
              <FormCheckbox label="Sync With DUX" name={`${name}.syncWithDUX`} disabled={!projectSyncWithDUX} />
              <FormInput label="Building Permit#" name={`${name}.buildingPermit`} styleField={{ width: '200px' }} />
              {onRemove && index !== undefined && (
                <div style={{}}>
                  {page && getValues(`${name}.isUnitCreationCompleted`) && !!getValues(`${name}.unitID`) && (
                    <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_UNIT]}>
                      <IconButton
                        style={{ padding: '10px 2px 5px 15px' }}
                        aria-label="view"
                        onClick={() => navigate(`/units/profile/${getValues(`${name}.unitID`)}`)}
                      >
                        <Tooltip title={<Typography variant="body1">View</Typography>}>
                          <img src={Icons.IconUnitsLotsDetails} alt="View" />
                        </Tooltip>
                      </IconButton>
                    </GuardedComponent>
                  )}
                </div>
              )}
              {!!getValues(`${name}.unitID`) && (
                <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_UNIT]}>
                  {onRemove &&
                    index !== undefined &&
                    (!getValues(`${name}.disableDelete`) ? (
                      <div style={{}}>
                        <DeleteButton
                          data={undefined}
                          deleteMutationConfig={() => projectsApiKeyStore.deleteLot(getValues(`${name}.unitID`))}
                          onDeleted={() => onRemove(index)}
                        >
                          <>
                            <Tooltip title={<Typography variant="body1">DELETE</Typography>}>
                              <img src={Icons.Delete} alt="DELETE" />
                            </Tooltip>
                          </>
                        </DeleteButton>
                      </div>
                    ) : (
                      <div style={{}}>
                        <DeleteButton
                          data={undefined}
                          deleteMutationConfig={() => projectsApiKeyStore.deleteLot(getValues(`${name}.unitID`))}
                          onDeleted={() => onRemove(index)}
                          isDisabled={true}
                        >
                          <>
                            <Tooltip title={<Typography variant="body1">DELETE</Typography>}>
                              <img src={Icons.DisabledDelete} alt="DELETE" />
                            </Tooltip>
                          </>
                        </DeleteButton>
                      </div>
                    ))}
                </GuardedComponent>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
